import { Pipe, PipeTransform } from '@angular/core';
import { MeasurementUnits, MeasureKind } from '../service-ui/measurement-units';

/**
 * Template-facing wrappers around `MeasurementUnits`.
 *
 * PURITY
 * ------
 * These are all **pure** pipes. A pure pipe only re-evaluates when its
 * input value changes, so in principle a display-unit switch would not
 * re-render an unchanged measurement. That is safe here because the
 * unit system is sourced from the current Organization, and changing
 * the Organization goes through `StartupService.selectRole()` /
 * `resetCurrentOrganization()`, which resets every data service and
 * navigates to `/secure/startup` -- the whole view tree is rebuilt.
 * Making them impure would run this on every change-detection cycle
 * for every measurement on screen (the packing UI renders hundreds)
 * for no benefit.
 *
 * NAMING
 * ------
 * `measureX` renders "value + unit"; `measureXValue` renders the
 * converted number only, for call sites like "8.5 × 11 in" where the
 * unit is emitted once after a pair.
 *
 * INPUT UNITS ARE ALWAYS CANONICAL
 * --------------------------------
 * Feed these the stored value, not a pre-converted one: inches for
 * `measureLength`, pounds for `measureWeight`, cubic inches for
 * `measureVolume`, points for `measurePoints`. The pipe does the
 * conversion.
 */

/** `{{ book.spineInches | measureLength:2 }}` -> "0.75 in" / "19.05 mm" */
@Pipe({ name: 'measureLength', pure: true })
export class MeasureLengthPipe implements PipeTransform {
  transform(inches: any, digits?: number): string {
    return MeasurementUnits.formatLength(inches, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/** `{{ w | measureLengthValue:1 }}×{{ h | measureLength:1 }}` -> "8.5×11 in" */
@Pipe({ name: 'measureLengthValue', pure: true })
export class MeasureLengthValuePipe implements PipeTransform {
  transform(inches: any, digits?: number): string {
    return MeasurementUnits.lengthValue(inches, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/** `{{ book.weight | measureWeight:2 }}` -> "0.55 lbs" / "0.25 kg" */
@Pipe({ name: 'measureWeight', pure: true })
export class MeasureWeightPipe implements PipeTransform {
  transform(pounds: any, digits?: number): string {
    return MeasurementUnits.formatWeight(pounds, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/** Converted weight number with no unit label. */
@Pipe({ name: 'measureWeightValue', pure: true })
export class MeasureWeightValuePipe implements PipeTransform {
  transform(pounds: any, digits?: number): string {
    return MeasurementUnits.weightValue(pounds, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/** `{{ metrics.totalBookVolume | measureVolume:0 }}` -> "100 in³" / "1639 cm³" */
@Pipe({ name: 'measureVolume', pure: true })
export class MeasureVolumePipe implements PipeTransform {
  transform(cubicInches: any, digits?: number): string {
    return MeasurementUnits.formatVolume(cubicInches, digits === undefined ? 0 : digits);
  }
}

/** Converted volume number with no unit label. */
@Pipe({ name: 'measureVolumeValue', pure: true })
export class MeasureVolumeValuePipe implements PipeTransform {
  transform(cubicInches: any, digits?: number): string {
    return MeasurementUnits.volumeValue(cubicInches, digits === undefined ? 0 : digits);
  }
}

/**
 * For values stored in points (1/72 in) -- book trim geometry such as
 * `book.body.width` / `book.body.height`.
 * `{{ book.body.width | measurePoints:1 }}` -> "8.5 in"
 */
@Pipe({ name: 'measurePoints', pure: true })
export class MeasurePointsPipe implements PipeTransform {
  transform(points: any, digits?: number): string {
    return MeasurementUnits.formatLengthFromPoints(points, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/** Converted points-to-display-length number with no unit label. */
@Pipe({ name: 'measurePointsValue', pure: true })
export class MeasurePointsValuePipe implements PipeTransform {
  transform(points: any, digits?: number): string {
    return MeasurementUnits.pointsValue(points, digits === undefined ? MeasurementUnits.DEFAULT_DIGITS : digits);
  }
}

/**
 * The translated unit label on its own, for headers and column
 * captions -- `<th>{{ 'weight' | unitLabel }}</th>`.
 *
 * This is the "separate the unit from the rest of the string" case
 * described in the class comment of `MeasurementUnits`: a phrase like
 * "COIL — eff. width in" is split into a translatable phrase
 * (`@@COIL effective width`) plus this label, so neither half has to
 * be re-translated when the unit system changes.
 */
@Pipe({ name: 'unitLabel', pure: true })
export class UnitLabelPipe implements PipeTransform {
  transform(kind: MeasureKind): string {
    return MeasurementUnits.label(kind);
  }
}





